import type {
  Product,
  RateCard,
  SubscriptionView,
} from "schemas/agent-billing";
import type { SubscriptionDraft } from "../models/SubscriptionDraft";

import * as React from "react";

import { DebugOverlay } from "@v2/features/admin/DebugOverlay";
import { useDebugOverlayIsVisible } from "@v2/features/admin/debugPanelChrome";
import QuantityControl from "@v2/features/agent-billing/shared/QuantityControl";
import { formatNumber, formatPrice } from "replo-utils/lib/format";
import { pluralize } from "replo-utils/lib/pluralize";

import {
  BillingSeparator,
  ConfirmFooter,
  LineItemRow,
} from "../pricing/BillingModalComponents";
import { price } from "../utils/price";
import { rateCard as rateCardUtils } from "../utils/rateCard";

const AddSubscriptionItemContent = ({
  currentSubscriptionDraft,
  product,
  currentCount,
  interval,
  currentPeriodEnd,
  rateCards,
  onConfirm,
  isLoading,
  maxQuantity,
  title,
}: {
  currentSubscriptionDraft: SubscriptionDraft;
  product: Product;
  currentCount: number;
  interval: "month" | "year";
  currentPeriodEnd: Date;
  rateCards: RateCard[];
  onConfirm: (args: {
    subscriptionView: SubscriptionView;
    additionalCount: number;
  }) => void | Promise<void>;
  isLoading: boolean;
  maxQuantity?: number;
  title?: React.ReactNode;
}) => {
  const [selectedQuantity, setSelectedQuantity] = React.useState(1);
  const { visible: isDebugView } = useDebugOverlayIsVisible();
  const lineItemsRef = React.useRef<HTMLDivElement>(null);

  const isIntervalYear = interval === "year";
  const addOnPrice = price.findDefaultByInterval({
    prices: product.prices,
    interval,
  });
  const nextBillingDate = currentPeriodEnd;
  const formattedNextBillingDate = nextBillingDate.toLocaleDateString(
    undefined,
    { month: "long", day: "numeric", year: "numeric" },
  );

  if (!addOnPrice) {
    return null;
  }

  const addOnPriceId = addOnPrice.priceId;
  const currentQuantityAtPrice = currentSubscriptionDraft.getItemQuantity({
    productType: product.productType,
    priceId: addOnPriceId,
  });

  const nextDraft = currentSubscriptionDraft.withItemQuantity({
    productType: product.productType,
    priceId: addOnPriceId,
    quantity: currentQuantityAtPrice + selectedQuantity,
  });

  const addOnPerMonth = price.getPerMonth(addOnPrice);
  const additionalMonthlyPrice = {
    amount: addOnPerMonth.amount * selectedQuantity,
    currency: addOnPerMonth.currency,
  };

  const currentRateCard = rateCards.find(
    (rateCard) => rateCard.plan === currentSubscriptionDraft.plan,
  );
  const nextRateCard = rateCards.find(
    (rateCard) => rateCard.plan === nextDraft.plan,
  );

  const proratedCharge =
    currentRateCard && nextRateCard
      ? rateCardUtils.getProratedCharge({
          currentRateCard,
          currentSubscriptionView: currentSubscriptionDraft.current,
          nextRateCard,
          nextSubscriptionView: nextDraft.current,
          nextBillingDate,
        })
      : null;

  const formattedAddOnPrice = `${formatPrice({ amount: addOnPrice.amount * selectedQuantity, currency: addOnPrice.currency })}/${interval}`;
  const confirmSubtext =
    isIntervalYear && proratedCharge
      ? `You'll be billed ${formatPrice(proratedCharge)} one time on your annual plan (${formattedAddOnPrice}) prorated until ${formattedNextBillingDate}.`
      : undefined;

  const additionalCount = selectedQuantity * (product.units ?? 1);

  return (
    <div className="flex flex-1 flex-col justify-between gap-6">
      <div ref={lineItemsRef} className="flex flex-col gap-4">
        <div className="text-3xl font-editorial">
          {title ?? `Add ${pluralize(product.displayName, 2)}`}
        </div>

        <LineItemRow
          label={
            <span className="text-muted">
              {formatNumber(currentCount)}{" "}
              {pluralize(product.displayName, currentCount)}
            </span>
          }
          value={<span className="font-medium text-muted">Current</span>}
        />

        <QuantityControl
          value={selectedQuantity}
          onIncrement={() => setSelectedQuantity((quantity) => quantity + 1)}
          onDecrement={() => setSelectedQuantity((quantity) => quantity - 1)}
          label={
            <span>
              {formatNumber(additionalCount)} Additional{" "}
              {pluralize(product.displayName, additionalCount)}
            </span>
          }
          minValue={1}
          maxValue={maxQuantity}
        />

        <BillingSeparator />

        <LineItemRow
          label={<span>Additional Monthly Total</span>}
          value={
            <span className="font-semibold">
              {formatPrice(additionalMonthlyPrice)}/mo
            </span>
          }
        />
      </div>
      <DebugOverlay
        isVisible={isDebugView}
        referenceRef={lineItemsRef}
        data={{
          productType: product.productType,
          priceId: addOnPriceId,
          interval,
          selectedQuantity,
          subscriptionItems: nextDraft.current.subscriptionItems,
        }}
      />

      <ConfirmFooter
        buttonText={`Confirm (${formatPrice(additionalMonthlyPrice)}/month)`}
        onConfirm={() => {
          return onConfirm({
            subscriptionView: nextDraft.current,
            additionalCount: additionalCount,
          });
        }}
        isLoading={isLoading}
        subtext={confirmSubtext}
      />
    </div>
  );
};

export default AddSubscriptionItemContent;
